/**
 * Reglas de la ficha de producto.
 *
 * Aparte de la pantalla porque tienen bordes —la pestaña que no existe, la que
 * existe pero no se puede ver— y una pantalla no se puede poner en un test sin
 * un browser.
 */

import { precioUnitario } from "../../lib/formato";

/**
 * Qué pestaña abrir, dado lo que dice la URL y cuáles hay.
 *
 * **Divergencia deliberada con `imprenta`** (ADR 0065): allá el cálculo es
 * `searchParams.get("tab") || "precios"`, sin validar. Con eso, un `?tab=`
 * inventado o un usuario sin `precios.read` —que no tiene esa pestaña— dejan el
 * contenido **en blanco**, sin error ni pista de qué pasó. Acá cualquier valor
 * que no esté disponible cae en la primera que sí lo esté.
 *
 * `null` solo si no hay ninguna, que hoy no puede pasar: Tamaños y Bases no
 * dependen de ningún permiso.
 */
export function pestanaActiva(
  desdeLaUrl: string | null,
  disponibles: readonly string[],
): string | null {
  if (desdeLaUrl && disponibles.includes(desdeLaUrl)) return desdeLaUrl;
  return disponibles[0] ?? null;
}

/**
 * Un precio de tarifa, con el formato de `PreciosSection` de `imprenta`.
 *
 * **Entre dos y cuatro decimales**, y eso no es capricho: el `numeric(_, 4)` de
 * la columna existe porque el precio por unidad de una tirada grande lleva
 * fracciones de centavo. Redondear a dos convertiría `$0,1234` en `$0,12`, y
 * sobre 10.000 unidades la cuenta se corre $34.
 *
 * Sin dato va `—` y no `$ 0,00`: la columna VIP está vacía cuando no hay precio
 * VIP, y un cero ahí diría que el cliente VIP no paga nada.
 */
export function precioDeTarifa(valor: unknown): string {
  if (typeof valor !== "number" && typeof valor !== "string") return "—";
  return precioUnitario(valor);
}

/**
 * El estado que se muestra en el listado.
 *
 * **No es el de `imprenta`**, que solo dice Activo/Inactivo. Acá el listado
 * responde además «¿se puede cotizar esto?», que es la pregunta que trae a
 * alguien a esta pantalla — y la respuesta tiene tres formas de ser que no.
 *
 * El orden importa: **sin tamaños no puede haber precios**, así que decir «Sin
 * precio» mandaría a cargar una tarifa a una pantalla donde no hay ninguna
 * combinación para elegir.
 */
export function estadoDeProducto(producto: {
  activo: boolean;
  tamanos: number;
  bases: number;
  precios: number;
}): { texto: string; tono: "ok" | "aviso" | "neutral" } {
  // Lo que hay que hacer con un producto dado de baja es activarlo, no
  // configurarlo: su estado gana sobre lo que le falte.
  if (!producto.activo) return { texto: "Inactivo", tono: "neutral" };
  if (producto.tamanos === 0 || producto.bases === 0) {
    return { texto: "Sin configurar", tono: "aviso" };
  }
  if (producto.precios === 0) return { texto: "Sin precio", tono: "aviso" };
  return { texto: "Activo", tono: "ok" };
}
